/**
 * The two enrolments behind one `DeviceVerificationPort`.
 *
 * Journal Lock asks one question — "did the platform verify the owner?" — and
 * the web can answer it two ways: a WebAuthn platform authenticator, or a
 * passphrase this build derives a key from. The gate must not know which,
 * because `planJournalLockChange` reasons about availability alone and a
 * two-shaped port would put that branch in every caller.
 *
 * The composition rule is "the first member that reports itself available
 * answers", not "try each in turn". Falling through from a declined WebAuthn
 * prompt into a passphrase field would raise a second challenge immediately
 * after the user said no, and a gate that keeps asking is a gate people learn
 * to dismiss without reading.
 */

import type {
  DeviceVerificationPort,
  JournalAuthReason,
  StringKeyValueStore,
  WebVerificationAvailability,
} from "@ff/domain/ports";
import type { PassphraseCrypto } from "./passphraseVault";
import { PassphraseKeyVault } from "./passphraseVault";
import type { WebAuthnEnvironment } from "./webauthn";
import { WebAuthnDeviceVerification } from "./webauthn";

/**
 * How the passphrase reaches the adapter.
 *
 * A `DeviceVerificationPort` cannot render a text field, and the field belongs
 * to the concealment curtain — the one surface that is already covering the
 * content. So the adapter is handed a function that resolves to what the user
 * typed, or to `null` for "Not now". Declining resolves rather than rejects for
 * the same reason the port never rejects: every non-verified outcome has to
 * land on one fail-closed path.
 */
export type PassphrasePrompt = (reason: JournalAuthReason) => Promise<string | null>;

export class PassphraseDeviceVerification implements DeviceVerificationPort {
  readonly #vault: PassphraseKeyVault;
  readonly #prompt: PassphrasePrompt;

  constructor(vault: PassphraseKeyVault, prompt: PassphrasePrompt) {
    this.#vault = vault;
    this.#prompt = prompt;
  }

  async availability(): Promise<WebVerificationAvailability> {
    // Unenrolled is unavailable, not "available and it will fail". The lock
    // toggle reads this to decide whether turning the lock on is even offered,
    // and offering a gate with no key behind it is how a user locks themselves
    // out of a journal they can still see.
    return this.#vault.isEnrolled() ? "passcode" : "unavailable";
  }

  async verify(reason: JournalAuthReason): Promise<boolean> {
    if (!this.#vault.isEnrolled()) return false;
    let entered: string | null;
    try {
      entered = await this.#prompt(reason);
    } catch {
      return false;
    }
    if (entered === null) return false;
    return (await this.#vault.unlock(entered)) === "unlocked";
  }
}

/**
 * Ordered fallback across enrolments.
 *
 * Order is the constructor's, so the caller states the preference rather than
 * this class encoding one: the browser's own authenticator is the better
 * experience where it exists, and the passphrase is what makes the feature
 * reachable at all where it does not.
 */
export class CompositeDeviceVerification implements DeviceVerificationPort {
  readonly #members: readonly DeviceVerificationPort[];

  constructor(members: readonly DeviceVerificationPort[]) {
    this.#members = members;
  }

  async availability(): Promise<WebVerificationAvailability> {
    for (const member of this.#members) {
      if ((await member.availability()) === "passcode") return "passcode";
    }
    return "unavailable";
  }

  async verify(reason: JournalAuthReason): Promise<boolean> {
    const active = await this.#activeMember();
    // No enrolment at all means no prompt — F-JP-05's "return false *without*
    // prompting". A prompt raised here would either no-op or, worse, look to
    // the user like it succeeded.
    if (active === null) return false;
    return active.verify(reason);
  }

  async #activeMember(): Promise<DeviceVerificationPort | null> {
    for (const member of this.#members) {
      if ((await member.availability()) === "passcode") return member;
    }
    return null;
  }
}

/**
 * The port for a build that has no verification capability at all.
 *
 * This is not a stub standing in for missing work: "this browser cannot verify
 * the device owner" is a real, expected state — an HTTP origin, a browser with
 * no platform authenticator and no passphrase enrolled — and the honest port
 * for it reports `unavailable` and verifies nothing. Every downstream decision
 * already handles that value, and handles it fail-closed.
 */
export const UNAVAILABLE_DEVICE_VERIFICATION: DeviceVerificationPort = {
  async availability(): Promise<WebVerificationAvailability> {
    return "unavailable";
  },
  async verify(): Promise<boolean> {
    return false;
  },
};

export interface BrowserDeviceVerificationOptions {
  /** Where the credential id, salt and check record live. Profile-wide. */
  readonly store: StringKeyValueStore;
  readonly prompt: PassphrasePrompt;
  readonly webAuthn?: WebAuthnEnvironment;
  readonly crypto?: PassphraseCrypto;
  /** Test-only cost reduction; see `PassphraseVaultOptions.iterations`. */
  readonly iterations?: number;
}

export interface BrowserDeviceVerification {
  readonly port: DeviceVerificationPort;
  /**
   * Exposed because the key it holds has a lifetime the port does not describe:
   * the privacy session drops it on `foregroundLost`, and the settings screen
   * enrols through it. The port stays the single verification interface.
   */
  readonly passphrase: PassphraseKeyVault;
  readonly webAuthn: WebAuthnDeviceVerification;
}

export function browserDeviceVerification(
  options: BrowserDeviceVerificationOptions,
): BrowserDeviceVerification {
  const passphrase = new PassphraseKeyVault({
    store: options.store,
    ...(options.crypto === undefined ? {} : { crypto: options.crypto }),
    ...(options.iterations === undefined ? {} : { iterations: options.iterations }),
  });
  const webAuthn = new WebAuthnDeviceVerification({
    store: options.store,
    ...(options.webAuthn === undefined ? {} : { environment: options.webAuthn }),
  });

  return {
    port: new CompositeDeviceVerification([
      webAuthn,
      new PassphraseDeviceVerification(passphrase, options.prompt),
    ]),
    passphrase,
    webAuthn,
  };
}
